import {
  ClientAccount,
  ClientAccountListRequest,
  CreateClientAccountReceiveOnlyRequest,
  CreateClientAccountStandardRequest,
  EditClientAccountRequest,
  PaginationMetaData,
} from 'vopay';
import { useState } from 'react';
import { useApi } from '#shared/providers/Api';

type CreateClientAccountRequest = CreateClientAccountStandardRequest | CreateClientAccountReceiveOnlyRequest;

export default function useClientAccounts() {
  const api = useApi();
  const [fetching, setFetching] = useState(false);
  const [clientAccount, setClientAccount] = useState<ClientAccount | null>(null);
  const [clientAccounts, setClientAccounts] = useState<ClientAccount[]>([]);

  const [pagination, setPagination] = useState<PaginationMetaData>({
    TotalRecords: 0,
    Limit: 0,
    Offset: 0,
  });

  async function createClientAccount(req: CreateClientAccountRequest, receiveOnly: boolean) {
    try {
      setFetching(true);

      const res = await (receiveOnly ? api.createClientAccountReceiveOnly(req) : api.createClientAccountStandard(req));

      if (!res.Success) throw new Error(res.ErrorMessage);

      return {
        clientAccountID: res.ClientAccountID,
      };
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  async function fetchClientAccount(clientAccountID: string) {
    try {
      setFetching(true);

      const res = await api.fetchClientAccountList({
        ClientAccountID: clientAccountID,
      });

      if (!res.Success) throw new Error(res.ErrorMessage);

      const found = res.ClientAccounts.find((a) => a.ClientAccountID === clientAccountID) || null;
      setClientAccount(found);

      return found;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  async function fetchClientAccounts(params: ClientAccountListRequest) {
    try {
      setFetching(true);
      const res = await api.fetchClientAccountList(params);

      if (!res.Success) throw new Error(res.ErrorMessage);

      setClientAccounts(res.ClientAccounts);
      setPagination(res.PaginationMetaData);

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  async function editClientAccount(params: EditClientAccountRequest) {
    try {
      setFetching(true);
      const res = await api.editClientAccount(params);

      if (!res.Success) throw new Error(res.ErrorMessage);

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  return {
    fetching,
    createClientAccount,
    clientAccount,
    fetchClientAccount,
    clientAccounts,
    fetchClientAccounts,
    editClientAccount,
    pagination,
  };
}
